import styled from 'styled-components';
import { theme } from '../theme';

// Base Button Styles
export const BaseButton = styled.button<{
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  disabled?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  border: 1px solid transparent;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily.primary};
  font-weight: ${theme.typography.fontWeight.medium};
  text-decoration: none;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  white-space: nowrap;
  
  &:focus {
    outline: 2px solid ${theme.colors.primary[500]};
    outline-offset: 2px;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  ${props => {
    // Size variants
    switch (props.size) {
      case 'sm':
        return `
          padding: ${theme.spacing[2]} ${theme.spacing[3]};
          font-size: ${theme.typography.fontSize.sm};
          min-height: 32px;
        `;
      case 'lg':
        return `
          padding: ${theme.spacing[4]} ${theme.spacing[6]};
          font-size: ${theme.typography.fontSize.lg};
          min-height: 48px;
        `;
      default:
        return `
          padding: ${theme.spacing[3]} ${theme.spacing[4]};
          font-size: ${theme.typography.fontSize.base};
          min-height: 40px;
        `;
    }
  }}
  
  ${props => props.fullWidth && `
    width: 100%;
  `}
  
  ${props => {
    // Color variants
    switch (props.variant) {
      case 'primary':
        return `
          background-color: ${theme.colors.primary[500]};
          color: ${theme.colors.text.inverse};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colors.primary[600]};
          }
          
          &:active:not(:disabled) {
            background-color: ${theme.colors.primary[700]};
          }
        `;
      case 'secondary':
        return `
          background-color: ${theme.colors.gray[100]};
          color: ${theme.colors.text.primary};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colors.gray[200]};
          }
          
          &:active:not(:disabled) {
            background-color: ${theme.colors.gray[300]};
          }
        `;
      case 'outline':
        return `
          background-color: transparent;
          color: ${theme.colors.primary[600]};
          border-color: ${theme.colors.primary[300]};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colors.primary[50]};
            border-color: ${theme.colors.primary[400]};
          }
          
          &:active:not(:disabled) {
            background-color: ${theme.colors.primary[100]};
          }
        `;
      case 'ghost':
        return `
          background-color: transparent;
          color: ${theme.colors.text.primary};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colors.gray[100]};
          }
          
          &:active:not(:disabled) {
            background-color: ${theme.colors.gray[200]};
          }
        `;
      case 'danger':
        return `
          background-color: ${theme.colors.error[500]};
          color: ${theme.colors.text.inverse};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colors.error[600]};
          }
          
          &:active:not(:disabled) {
            background-color: ${theme.colors.error[700]};
          }
        `;
      default:
        return `
          background-color: ${theme.colors.primary[500]};
          color: ${theme.colors.text.inverse};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colors.primary[600]};
          }
          
          &:active:not(:disabled) {
            background-color: ${theme.colors.primary[700]};
          }
        `;
    }
  }}
`;

export const IconButton = styled(BaseButton)`
  padding: ${theme.spacing[2]};
  min-width: auto;
  aspect-ratio: 1;
`;

export const ButtonGroup = styled.div<{ orientation?: 'horizontal' | 'vertical' }>`
  display: flex;
  flex-direction: ${props => props.orientation === 'vertical' ? 'column' : 'row'};
  
  ${BaseButton} {
    ${props => props.orientation === 'vertical' ? `
      &:not(:first-child) {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
        margin-top: -1px;
      }
      
      &:not(:last-child) {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    ` : `
      &:not(:first-child) {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        margin-left: -1px;
      }
      
      &:not(:last-child) {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
    `}
  }
`;
